import {
  CallMerge as CallMergeIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Place as PlaceIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material'
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import type { Contact, ContactOwnerId, ContactStage } from '@otw/models/leads/contacts'
import { formatContactBirthday } from '@otw/models/leads/contacts'
import React, { useEffect, useMemo, useState } from 'react'
import type { MergeContactsFieldSources } from 'hooks/contacts/useContacts'
import { useContactCompanies } from 'hooks/contacts/useContactCompanies'
import { useUsers } from 'hooks/useUsers'
import {
  getPickupLocationLabel,
} from '../../util/pickupLocationDisplay'
import { resolveOwnerDisplayName } from '../../util/resolveOwnerDisplayName'
import {
  getDefaultMergeContactFieldSources,
  getDefaultSecondaryEmailForMerge,
  getMergeEmailCandidates,
  mergeContactLeadIds,
  type MergeContactsFieldKey,
  type MergeFieldSource,
} from '../../util/mergeContacts'
import { getContactMemberCompanyIds } from '../../util/contactCompanyMembership'
import { CONTACT_EMAIL_PATTERN, normalizeContactEmail } from '../../util/contactIdentity'
import { useSoldVehiclesForCrm } from 'hooks/inventory/useSoldVehiclesForCrm'

interface MergeContactsDialogProps {
  open: boolean
  onClose: () => void
  contacts: Contact[]
  initialPrimaryId?: string | null
  initialSecondaryId?: string | null
  onMerge: (input: {
    primaryContactId: string
    secondaryContactId: string
    fieldSources: MergeContactsFieldSources
    primaryEmailOverride?: string
    secondaryEmailOverride?: string
  }) => Promise<void>
  isMerging?: boolean
}

type MergeFieldConfig = {
  key: MergeContactsFieldKey
  label: string
  format: (contact: Contact) => string
}

const STAGE_LABEL: Record<ContactStage, string> = {
  prospect: 'Prospect',
  opportunity: 'Opportunity',
  customer: 'Customer',
  unqualified: 'Unqualified',
}

const getOwnerLabel = (
  ownerId: ContactOwnerId,
  users: Parameters<typeof resolveOwnerDisplayName>[1],
): string => resolveOwnerDisplayName(ownerId, users, `Owner #${ownerId}`)

const formatOptional = (value: string | undefined): string => {
  const trimmed = value?.trim()
  return trimmed || '—'
}

const MergeContactsDialog: React.FC<MergeContactsDialogProps> = ({
  open,
  onClose,
  contacts,
  initialPrimaryId = null,
  initialSecondaryId = null,
  onMerge,
  isMerging = false,
}) => {
  const theme = useTheme()
  const { companies: contactCompanies } = useContactCompanies()
  const { users: allUsers } = useUsers()

  const sortedContacts = useMemo(
    () => [...contacts].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [contacts],
  )

  const [primaryContact, setPrimaryContact] = useState<Contact | null>(null)
  const [secondaryContact, setSecondaryContact] = useState<Contact | null>(null)
  const [fieldSources, setFieldSources] = useState<MergeContactsFieldSources | null>(null)
  const [primaryEmailDraft, setPrimaryEmailDraft] = useState('')
  const [secondaryEmailDraft, setSecondaryEmailDraft] = useState('')
  const [secondaryEmailTouched, setSecondaryEmailTouched] = useState(false)

  const primarySoldQuery = useMemo(
    () => ({
      contactIds: primaryContact?.id ? [primaryContact.id] : [],
      userIds: primaryContact?.userId?.trim() ? [primaryContact.userId.trim()] : [],
    }),
    [primaryContact?.id, primaryContact?.userId],
  )
  const secondarySoldQuery = useMemo(
    () => ({
      contactIds: secondaryContact?.id ? [secondaryContact.id] : [],
      userIds: secondaryContact?.userId?.trim() ? [secondaryContact.userId.trim()] : [],
    }),
    [secondaryContact?.id, secondaryContact?.userId],
  )
  const { soldVehicles: primarySoldVehicles } = useSoldVehiclesForCrm(primarySoldQuery)
  const { soldVehicles: secondarySoldVehicles } = useSoldVehiclesForCrm(secondarySoldQuery)

  useEffect(() => {
    if (!open) return
    const primary = initialPrimaryId
      ? sortedContacts.find((c) => c.id === initialPrimaryId) ?? null
      : null
    const secondary = initialSecondaryId
      ? sortedContacts.find((c) => c.id === initialSecondaryId) ?? null
      : null
    setPrimaryContact(primary)
    setSecondaryContact(secondary)
    setSecondaryEmailDraft('')
    setSecondaryEmailTouched(false)
  }, [open, initialPrimaryId, initialSecondaryId, sortedContacts])

  useEffect(() => {
    if (!primaryContact || !secondaryContact) {
      setFieldSources(null)
      setSecondaryEmailDraft('')
      setSecondaryEmailTouched(false)
      return
    }
    if (primaryContact.id === secondaryContact.id) {
      setFieldSources(null)
      setSecondaryEmailDraft('')
      setSecondaryEmailTouched(false)
      return
    }
    const nextSources = getDefaultMergeContactFieldSources(primaryContact, secondaryContact)
    setFieldSources(nextSources)
    setSecondaryEmailTouched(false)
    const candidates = getMergeEmailCandidates(primaryContact, secondaryContact)
    const defaultPrimary =
      (nextSources.email === 'primary'
        ? primaryContact.email?.trim()
        : secondaryContact.email?.trim()) ||
      candidates[0] ||
      ''
    setPrimaryEmailDraft(defaultPrimary)
    setSecondaryEmailDraft(
      getDefaultSecondaryEmailForMerge(primaryContact, secondaryContact, nextSources.email) ?? '',
    )
  }, [primaryContact, secondaryContact])

  const fieldConfigs = useMemo<MergeFieldConfig[]>(
    () => [
      { key: 'name', label: 'Name', format: (c) => formatOptional(c.name) },
      { key: 'phone', label: 'Phone', format: (c) => formatOptional(c.phone) },
      {
        key: 'birthday',
        label: 'Birthday',
        format: (c) => formatContactBirthday(c.birthday) || '—',
      },
      {
        key: 'ownerId',
        label: 'Sales owner',
        format: (c) => getOwnerLabel(c.ownerId, allUsers),
      },
      {
        key: 'inventoryOwnerId',
        label: 'Inventory owner',
        format: (c) => getOwnerLabel(c.inventoryOwnerId ?? null, allUsers),
      },
      {
        key: 'stage',
        label: 'Sales stage',
        format: (c) => STAGE_LABEL[c.stage],
      },
      {
        key: 'inventoryStage',
        label: 'Inventory stage',
        format: (c) => STAGE_LABEL[c.inventoryStage ?? 'prospect'],
      },
      {
        key: 'userId',
        label: 'Portal account',
        format: (c) => (c.userId?.trim() ? 'Linked' : 'No account'),
      },
      {
        key: 'dropoffLocation',
        label: 'Dropoff address',
        format: (c) =>
          c.dropoffLocation ? getPickupLocationLabel(c.dropoffLocation) : '—',
      },
    ],
    [allUsers],
  )

  const mergePreview = useMemo(() => {
    if (!primaryContact || !secondaryContact || primaryContact.id === secondaryContact.id) {
      return null
    }
    const mergedCompanyIds = new Set([
      ...getContactMemberCompanyIds(primaryContact.id, contactCompanies),
      ...getContactMemberCompanyIds(secondaryContact.id, contactCompanies),
    ])
    const activityCount =
      (primaryContact.activities?.length ?? 0) + (secondaryContact.activities?.length ?? 0)
    const leadCount = mergeContactLeadIds(
      primaryContact.leadIds ?? [],
      secondaryContact.leadIds ?? [],
    ).length
    return {
      companyCount: mergedCompanyIds.size,
      activityCount,
      leadCount,
      primarySoldCount: primarySoldVehicles.length,
      secondarySoldCount: secondarySoldVehicles.length,
      primarySoldStocks: primarySoldVehicles
        .map((v) => v.stock_number || v.id)
        .slice(0, 8),
      secondarySoldStocks: secondarySoldVehicles
        .map((v) => v.stock_number || v.id)
        .slice(0, 8),
    }
  }, [
    primaryContact,
    secondaryContact,
    contactCompanies,
    primarySoldVehicles,
    secondarySoldVehicles,
  ])

  const portalAccountConflict = useMemo(() => {
    if (!primaryContact || !secondaryContact) return false
    const primaryUserId = primaryContact.userId?.trim() ?? ''
    const secondaryUserId = secondaryContact.userId?.trim() ?? ''
    return Boolean(primaryUserId && secondaryUserId && primaryUserId !== secondaryUserId)
  }, [primaryContact, secondaryContact])

  const emailCandidates = useMemo(() => {
    if (!primaryContact || !secondaryContact) return []
    return getMergeEmailCandidates(primaryContact, secondaryContact)
  }, [primaryContact, secondaryContact])

  const chosenPrimaryEmail = primaryEmailDraft.trim()

  const secondaryEmailTrim = secondaryEmailDraft.trim()
  const secondaryEmailInvalid =
    secondaryEmailTrim.length > 0 && !CONTACT_EMAIL_PATTERN.test(secondaryEmailTrim)
  const primaryEmailInvalid =
    chosenPrimaryEmail.length > 0 && !CONTACT_EMAIL_PATTERN.test(chosenPrimaryEmail)
  const emailsMatchEachOther =
    secondaryEmailTrim.length > 0 &&
    chosenPrimaryEmail.length > 0 &&
    normalizeContactEmail(secondaryEmailTrim) === normalizeContactEmail(chosenPrimaryEmail)

  const canMerge =
    Boolean(primaryContact && secondaryContact) &&
    primaryContact!.id !== secondaryContact!.id &&
    fieldSources != null &&
    !primaryEmailInvalid &&
    !secondaryEmailInvalid &&
    !emailsMatchEachOther

  const handleFieldSourceChange = (key: MergeContactsFieldKey, value: string) => {
    if (!fieldSources) return
    setFieldSources({
      ...fieldSources,
      [key]: value as MergeFieldSource,
    })
  }

  const pickDefaultSecondaryForPrimary = (nextPrimary: string) => {
    if (!primaryContact || !secondaryContact) return ''
    const primaryNorm = normalizeContactEmail(nextPrimary)
    const other = emailCandidates.find(
      (email) => normalizeContactEmail(email) !== primaryNorm,
    )
    return other ?? ''
  }

  const handlePrimaryEmailSelect = (email: string) => {
    const emailNorm = normalizeContactEmail(email)
    const secondaryNorm = normalizeContactEmail(secondaryEmailDraft)
    // Picking the current secondary as primary → move primary into secondary.
    if (emailNorm && emailNorm === secondaryNorm) {
      const previousPrimary = primaryEmailDraft.trim()
      if (previousPrimary && normalizeContactEmail(previousPrimary) !== emailNorm) {
        setSecondaryEmailDraft(previousPrimary)
        setSecondaryEmailTouched(true)
      } else {
        setSecondaryEmailDraft(pickDefaultSecondaryForPrimary(email))
        setSecondaryEmailTouched(true)
      }
      setPrimaryEmailDraft(email)
      return
    }
    setPrimaryEmailDraft(email)
    if (!secondaryEmailTouched) {
      setSecondaryEmailDraft(pickDefaultSecondaryForPrimary(email))
    }
  }

  const handleSecondaryEmailSelect = (email: string) => {
    setSecondaryEmailTouched(true)
    const emailNorm = normalizeContactEmail(email)
    const primaryNorm = normalizeContactEmail(primaryEmailDraft)
    // Picking the current primary as secondary → move secondary into primary.
    if (emailNorm && emailNorm === primaryNorm) {
      const previousSecondary = secondaryEmailDraft.trim()
      if (previousSecondary && normalizeContactEmail(previousSecondary) !== emailNorm) {
        setPrimaryEmailDraft(previousSecondary)
        setSecondaryEmailDraft(email)
        return
      }
      const other = emailCandidates.find(
        (candidate) => normalizeContactEmail(candidate) !== emailNorm,
      )
      if (other) {
        setPrimaryEmailDraft(other)
        setSecondaryEmailDraft(email)
        return
      }
      // No alternate primary available — keep the current pair as-is.
      return
    }
    setSecondaryEmailDraft(email)
  }

  const handleMerge = async () => {
    if (!primaryContact || !secondaryContact || !fieldSources) return
    if (primaryEmailInvalid || secondaryEmailInvalid || emailsMatchEachOther) return
    await onMerge({
      primaryContactId: primaryContact.id,
      secondaryContactId: secondaryContact.id,
      fieldSources,
      primaryEmailOverride: primaryEmailDraft,
      secondaryEmailOverride: secondaryEmailDraft,
    })
  }

  const primaryOptions = sortedContacts.filter((c) => c.id !== secondaryContact?.id)
  const secondaryOptions = sortedContacts.filter((c) => c.id !== primaryContact?.id)

  const renderValueCell = (
    value: string,
    selected: boolean,
    radioValue: string,
  ) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        flex: 1,
        minWidth: 0,
        borderRadius: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <FormControlLabel
        value={radioValue}
        control={<Radio size="small" />}
        label={
          <Typography
            variant="body2"
            sx={{
              wordBreak: 'break-word',
              fontWeight: selected ? 600 : 400,
              color: selected ? 'text.primary' : 'text.secondary',
            }}
          >
            {value}
          </Typography>
        }
        sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
      />
    </Paper>
  )

  return (
    <Dialog open={open} onClose={() => !isMerging && onClose()} maxWidth="md" fullWidth>
      <Box
        sx={{
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: 'primary.contrastText',
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CallMergeIcon />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Merge contacts
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Combine duplicate people and choose which details to keep.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            disabled={isMerging}
            size="small"
            aria-label="Close"
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ bgcolor: 'background.default' }}>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Select contacts
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                options={primaryOptions}
                value={primaryContact}
                onChange={(_, value) => setPrimaryContact(value)}
                getOptionLabel={(option) => option.name || 'Unnamed contact'}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={isMerging}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Keep this record (primary)"
                    size="small"
                    helperText="This contact stays in the system after the merge."
                  />
                )}
              />
              <Autocomplete
                options={secondaryOptions}
                value={secondaryContact}
                onChange={(_, value) => setSecondaryContact(value)}
                getOptionLabel={(option) => option.name || 'Unnamed contact'}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={isMerging}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Merge into primary (duplicate)"
                    size="small"
                    helperText="This record will be removed after its data is combined."
                  />
                )}
              />
            </Stack>
          </Paper>

          {primaryContact && secondaryContact && primaryContact.id === secondaryContact.id && (
            <Alert severity="warning">Choose two different contacts.</Alert>
          )}

          {canMerge && mergePreview && fieldSources && (
            <>
              <Alert
                severity="info"
                icon={<WarningIcon />}
                sx={{ '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {secondaryContact!.name} will be deleted after merging into{' '}
                  {primaryContact!.name}.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    icon={<GroupIcon />}
                    label={`${mergePreview.companyCount} compan${
                      mergePreview.companyCount === 1 ? 'y' : 'ies'
                    } combined`}
                  />
                  <Chip
                    size="small"
                    icon={<HistoryIcon />}
                    label={`${mergePreview.activityCount} activit${
                      mergePreview.activityCount === 1 ? 'y' : 'ies'
                    } combined`}
                  />
                  <Chip
                    size="small"
                    label={`${mergePreview.leadCount} linked lead${
                      mergePreview.leadCount === 1 ? '' : 's'
                    }`}
                  />
                  <Chip
                    size="small"
                    color={mergePreview.primarySoldCount > 0 ? 'info' : 'default'}
                    label={`${primaryContact!.name}: ${mergePreview.primarySoldCount} sold truck${
                      mergePreview.primarySoldCount === 1 ? '' : 's'
                    }`}
                  />
                  <Chip
                    size="small"
                    color={mergePreview.secondarySoldCount > 0 ? 'warning' : 'default'}
                    label={`${secondaryContact!.name}: ${mergePreview.secondarySoldCount} sold truck${
                      mergePreview.secondarySoldCount === 1 ? '' : 's'
                    }`}
                  />
                </Stack>
                {(mergePreview.primarySoldStocks.length > 0 ||
                  mergePreview.secondarySoldStocks.length > 0) && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {mergePreview.primarySoldStocks.length > 0
                      ? `Keep (${primaryContact!.name}): ${mergePreview.primarySoldStocks.join(', ')}. `
                      : ''}
                    {mergePreview.secondarySoldStocks.length > 0
                      ? `Moving from ${secondaryContact!.name}: ${mergePreview.secondarySoldStocks.join(', ')}.`
                      : ''}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Company memberships and tasks linked to the duplicate will move to the primary
                  contact.
                </Typography>
              </Alert>

              {portalAccountConflict && (
                <Alert severity="warning">
                  Both contacts have different portal client accounts. Choose which account to
                  keep — the other link will be removed.
                </Alert>
              )}

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Choose values for the merged contact
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mb: 2, display: { xs: 'none', sm: 'flex' } }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      size="small"
                      icon={<PersonIcon />}
                      label={primaryContact!.name}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      size="small"
                      icon={<PersonIcon />}
                      label={secondaryContact!.name}
                      variant="outlined"
                    />
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  {emailCandidates.length > 0 && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        sx={{ display: 'block', mb: 0.75 }}
                      >
                        PRIMARY EMAIL
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          emailCandidates.find(
                            (email) =>
                              normalizeContactEmail(email) ===
                              normalizeContactEmail(primaryEmailDraft),
                          ) ?? primaryEmailDraft
                        }
                        onChange={(e) => handlePrimaryEmailSelect(e.target.value)}
                        sx={{ gap: 1, flexWrap: 'wrap' }}
                      >
                        {emailCandidates.map((email) =>
                          renderValueCell(
                            email,
                            normalizeContactEmail(primaryEmailDraft) ===
                              normalizeContactEmail(email),
                            email,
                          ),
                        )}
                      </RadioGroup>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        sx={{ display: 'block', mt: 1.5, mb: 0.75 }}
                      >
                        SECONDARY EMAIL
                      </Typography>
                      <RadioGroup
                        row
                        value={
                          emailCandidates.find(
                            (email) =>
                              normalizeContactEmail(email) ===
                              normalizeContactEmail(secondaryEmailDraft),
                          ) ?? secondaryEmailDraft
                        }
                        onChange={(e) => handleSecondaryEmailSelect(e.target.value)}
                        sx={{ gap: 1, flexWrap: 'wrap' }}
                      >
                        {emailCandidates.map((email) =>
                          renderValueCell(
                            email,
                            normalizeContactEmail(secondaryEmailDraft) ===
                              normalizeContactEmail(email),
                            email,
                          ),
                        )}
                      </RadioGroup>
                      {(primaryEmailInvalid || secondaryEmailInvalid || emailsMatchEachOther) && (
                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.75 }}>
                          {primaryEmailInvalid || secondaryEmailInvalid
                            ? 'Enter a valid email address'
                            : 'Secondary email must differ from primary email'}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.75 }}
                      >
                        Choose from up to four addresses (primary and secondary from each contact).
                      </Typography>
                    </Box>
                  )}

                  {fieldConfigs.map((field) => {
                    const primaryValue = field.format(primaryContact!)
                    const secondaryValue = field.format(secondaryContact!)
                    const source = fieldSources[field.key]
                    const valuesDiffer =
                      primaryValue.trim().toLowerCase() !==
                      secondaryValue.trim().toLowerCase()

                    return (
                      <Box key={field.key}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 0.75 }}
                        >
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            {field.label.toUpperCase()}
                          </Typography>
                          {!valuesDiffer && primaryValue !== '—' && (
                            <Chip label="Same" size="small" sx={{ height: 18, fontSize: 11 }} />
                          )}
                        </Stack>
                        <RadioGroup
                          row
                          value={source}
                          onChange={(e) =>
                            handleFieldSourceChange(field.key, e.target.value)
                          }
                          sx={{ gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                        >
                          {renderValueCell(primaryValue, source === 'primary', 'primary')}
                          {renderValueCell(secondaryValue, source === 'secondary', 'secondary')}
                        </RadioGroup>
                      </Box>
                    )
                  })}
                </Stack>
              </Paper>

              {fieldSources.name === 'secondary' && (
                <Alert severity="warning" icon={<PlaceIcon />}>
                  The surviving contact record will use the duplicate&apos;s name.
                </Alert>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isMerging}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CallMergeIcon />}
          disabled={!canMerge || isMerging}
          onClick={() => void handleMerge()}
        >
          {isMerging ? 'Merging…' : 'Merge contacts'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MergeContactsDialog
